'use client'

import { cn } from '@brew/ui/cn'
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@brew/ui/sheet'
import { ArrowUpRight, Check, ChevronDown, X } from 'lucide-react'
import Link from 'next/link'
import { useTranslations } from 'next-intl'
import { useEffect, useRef, useState } from 'react'
import { useAppLocale } from '@/components/i18n/locale-provider'
import {
  type FeatureComparisonColumn,
  type FeatureComparisonSection,
  FeatureComparisonTable,
} from '@/components/marketing/feature-comparison-table'
import {
  ENTERPRISE_PAGE_HREF,
  getPlanCtaLabel,
  type PricingTranslator,
} from '@/components/marketing/sections/pricing-comparison-labels'
import { MarketingContainer } from '@/components/marketing/v2/marketing-container'
import {
  MARKETING_CANVAS_BG,
  MARKETING_GUTTER_X,
  MARKETING_SECTION_HEADING,
  MARKETING_SECTION_Y,
} from '@/components/marketing/v2/marketing-layout'
import { PublicSignUpButton } from '@/components/public-sign-up-button'
import { pricingCtaClass } from '@/components/ui/pricing-cta'
import { BILLING_PLANS, SELF_SERVE_PLAN_KEYS } from '@/lib/billing/plans'
import {
  defaultPriceSummary,
  formatCurrency,
} from '@/lib/billing/price-summary'
import type { Locale } from '@/lib/i18n/config'
import { localizePath } from '@/lib/i18n/routing'
import type { PricingComparison } from '@/lib/site/marketing-content'

/**
 * The highlighted column, matched BY NAME. It used to be `plans[2]` / a
 * literal `index === 2`, which silently pointed at the wrong plan the moment
 * a column was added or removed (sunsetting Starter shifted it onto Pro).
 */
const FEATURED_PLAN_NAME = BILLING_PLANS.growth.name

const PRICE_SUMMARIES = defaultPriceSummary()

/** Free costs nothing, in the paid plans' currency. */
const FREE_PRICE = { amount: 0, currency: 'USD' } as const

/** A plan's monthly list price in the reader's language ("$99/month"). */
function getPlanPrice(t: PricingTranslator, locale: Locale, plan: string) {
  if (plan === BILLING_PLANS.free.name) {
    return t('comparison.perMonth', {
      price: formatCurrency({ ...FREE_PRICE, locale }),
    })
  }

  const selfServeKey = SELF_SERVE_PLAN_KEYS.find(
    (key) => BILLING_PLANS[key].name === plan
  )
  const price = selfServeKey ? PRICE_SUMMARIES[selfServeKey] : null
  if (!price) {
    return t('comparison.customPrice')
  }

  // The headline stays the MONTH-TO-MONTH price on every surface — the
  // discount requires the prepaid term, which the cards offer as a separate
  // CTA. Showing the discounted rate here would advertise a price this table
  // cannot explain the conditions for.
  return t('comparison.perMonth', {
    price: formatCurrency({
      amount: price.amount,
      currency: price.currency,
      locale,
    }),
  })
}

function ComparisonPlanCta({
  plan,
  label,
  featured,
}: {
  plan: string
  label: string
  featured: boolean
}) {
  const locale = useAppLocale()
  const className = pricingCtaClass({
    tone: featured ? 'featured' : 'outline',
    size: 'sm',
  })

  if (plan === BILLING_PLANS.enterprise.name) {
    return (
      <Link
        href={localizePath(locale, ENTERPRISE_PAGE_HREF)}
        className={cn(className, 'gap-1.5')}
      >
        {label}
        <ArrowUpRight className="size-4" aria-hidden="true" />
      </Link>
    )
  }

  return <PublicSignUpButton className={className}>{label}</PublicSignUpButton>
}

function renderFeatureValue(value: string) {
  if (value === 'check') {
    return (
      <Check
        className="mx-auto size-3.5 text-muted-foreground"
        strokeWidth={2}
        aria-hidden="true"
      />
    )
  }

  if (value === 'dash') {
    return <span className="text-lg text-muted-foreground/50">–</span>
  }

  return (
    <span className="font-medium text-foreground tabular-nums">{value}</span>
  )
}

/**
 * The `/pricing` plan comparison. Its chrome translates with `pricingPage`,
 * which the page hands it (`ClientMessages`); the page builds the table
 * itself on the server (`buildPricingComparison`), so the `pricingContent`
 * catalog never ships to the browser.
 */
export function PricingComparisonTable({
  table,
}: {
  table: PricingComparison
}) {
  const t = useTranslations('pricingPage')
  const locale = useAppLocale()
  const { plans, sections: configSections } = table
  const [selectedPlan, setSelectedPlan] = useState<string>(FEATURED_PLAN_NAME)
  const [isDrawerOpen, setIsDrawerOpen] = useState(false)
  const [isSticky, setIsSticky] = useState(false)
  const sentinelRef = useRef<HTMLDivElement>(null)

  // detect when thead becomes sticky
  useEffect(() => {
    const sentinel = sentinelRef.current
    if (!sentinel) {
      return
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsSticky(!entry.isIntersecting)
      },
      { threshold: 0, rootMargin: '-64px 0px 0px 0px' }
    )

    observer.observe(sentinel)
    return () => observer.disconnect()
  }, [])

  const handlePlanSelect = (plan: string) => {
    setSelectedPlan(plan)
    setIsDrawerOpen(false)
  }

  const columns: Array<FeatureComparisonColumn> = [
    {
      key: 'feature',
      className: cn(
        'px-3 py-4 max-md:hidden md:table-cell',
        !isSticky && 'rounded-tl-2xl'
      ),
    },
    ...plans.map((plan): FeatureComparisonColumn => {
      const isSelectedPlan = plan === selectedPlan
      const isFeaturedPlan = plan === FEATURED_PLAN_NAME
      return {
        key: plan,
        className: cn(
          'px-3 py-4',
          'md:table-cell',
          !isSticky && 'md:last:rounded-tr-2xl',
          plan !== selectedPlan && 'max-md:hidden',
          isSelectedPlan && 'max-md:block max-md:w-full'
        ),
        children: (
          <div className="flex flex-col gap-3">
            {/* Desktop: Plan name as text */}
            <span className="font-medium text-[15px] text-foreground tracking-tight max-md:hidden">
              {plan}
            </span>

            {/* Mobile: Clickable plan selector button */}
            <button
              type="button"
              onClick={() => setIsDrawerOpen(true)}
              className={cn(
                'flex w-full items-center justify-between rounded-lg border border-border px-3 py-2 text-left font-medium text-foreground text-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-ring md:hidden',
                MARKETING_CANVAS_BG,
                plan !== selectedPlan && 'max-md:hidden'
              )}
            >
              <span>{selectedPlan}</span>
              <ChevronDown className="size-4" aria-hidden="true" />
            </button>
            <p className="text-foreground/70 text-xs">
              {getPlanPrice(t, locale, plan)}
            </p>
            <ComparisonPlanCta
              plan={plan}
              label={getPlanCtaLabel(t, plan)}
              featured={isFeaturedPlan}
            />
          </div>
        ),
      }
    }),
  ]

  const sections: Array<FeatureComparisonSection> = configSections.map(
    (section) => ({
      key: section.name,
      heading: {
        rowClassName: cn('section-row sticky top-48 z-10', MARKETING_CANVAS_BG),
        children: (
          <div className="bg-muted/30 px-3 py-3 text-muted-foreground tracking-wider">
            {section.name}
          </div>
        ),
      },
      rows: section.features.map((feature) => ({
        key: feature.name,
        className: 'group/row max-md:flex max-md:w-full',
        cells: [
          {
            className: cn(
              'px-3 py-3 font-medium text-muted-foreground text-sm group-last/row:rounded-bl-2xl group-hover/row:bg-muted max-md:flex max-md:w-2/5 max-md:items-center md:table-cell',
              MARKETING_CANVAS_BG
            ),
            children: feature.name,
          },
          ...feature.values.map((value, index) => ({
            className: cn(
              'px-3 py-3 text-center text-sm group-last/row:last:rounded-br-2xl group-hover/row:bg-muted',
              MARKETING_CANVAS_BG,
              'max-md:flex max-md:w-3/5 max-md:items-center max-md:justify-center md:table-cell',
              plans[index] !== selectedPlan && 'max-md:hidden'
            ),
            children: renderFeatureValue(value),
          })),
        ],
      })),
    })
  )

  return (
    <section
      id="compare-plans"
      className={cn(
        'relative w-full border-border border-t',
        MARKETING_GUTTER_X,
        MARKETING_SECTION_Y
      )}
    >
      <MarketingContainer>
        <h2
          className={cn(
            MARKETING_SECTION_HEADING,
            'mb-10 text-center text-foreground'
          )}
        >
          {t('comparison.heading')}
        </h2>

        {/* to detect when thead becomes sticky */}
        <div ref={sentinelRef} className="h-0 w-0" aria-hidden="true" />

        <div className="relative">
          <div
            aria-hidden="true"
            className={cn(
              'pointer-events-none sticky top-0 h-18 w-full transition-opacity',
              isSticky ? 'opacity-100' : 'opacity-0'
            )}
          >
            <div
              className="absolute inset-0 h-full w-full bg-gradient-to-b from-cream/50 to-cream backdrop-blur-sm dark:from-background/50 dark:to-background"
              style={{
                maskImage:
                  'linear-gradient(to top, var(--cream) 100%, transparent)',
                WebkitMaskImage:
                  'linear-gradient(to top, var(--cream) 100%, transparent)',
              }}
            />
          </div>

          <FeatureComparisonTable
            className="-mt-16 w-full border-collapse"
            headClassName={cn('sticky top-16 z-20', MARKETING_CANVAS_BG)}
            columns={columns}
            sections={sections}
          />
        </div>
      </MarketingContainer>

      {/* Mobile Plan Selector Drawer */}
      <Sheet open={isDrawerOpen} onOpenChange={setIsDrawerOpen}>
        <SheetContent
          side="bottom"
          aria-describedby={undefined}
          overlayClassName="z-40 backdrop-blur-xs"
          className="bottom-3 z-40 mx-auto max-h-[80vh] w-[95%] overflow-y-auto rounded-xl border border-border p-4"
        >
          <SheetHeader className="flex-row items-center justify-between p-0 text-muted-foreground">
            <SheetTitle className="font-medium text-muted-foreground text-sm">
              {t('comparison.selectPlan')}
            </SheetTitle>
            <SheetClose
              aria-label={t('comparison.close')}
              className="cursor-pointer rounded-lg p-1"
            >
              <X className="size-5" />
            </SheetClose>
          </SheetHeader>
          <div className="flex flex-col gap-2">
            {plans.map((plan) => (
              <button
                key={plan}
                type="button"
                onClick={() => handlePlanSelect(plan)}
                className={cn(
                  'w-full rounded-lg px-4 py-3 text-left transition-colors',
                  'border border-border',
                  plan === selectedPlan
                    ? 'border-foreground bg-muted/40 font-medium text-foreground'
                    : 'bg-background hover:bg-muted'
                )}
              >
                <div className="flex items-center justify-between">
                  <span className="font-medium text-base">{plan}</span>
                  {plan === selectedPlan && (
                    <Check
                      className="size-4 text-muted-foreground"
                      aria-hidden="true"
                    />
                  )}
                </div>
                <p className="mt-1 text-muted-foreground text-sm">
                  {getPlanPrice(t, locale, plan)}
                </p>
              </button>
            ))}
          </div>
        </SheetContent>
      </Sheet>
    </section>
  )
}
